import React from 'react';
import { observer, inject } from 'mobx-react';
import { rrulestr } from 'rrule';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import { currencyFormattedAmount } from 'helpers/currency';
import { currentTime } from 'helpers/time';
import * as DataList from 'components/DataList';
import { accentFor } from 'components/Tables/ScheduleList';
import { SERVICE_AGREEMENT_COLUMNS, SERVICE_AGREEMENT_CARDS } from 'components/Tables/ServiceAgreements';

// A client's service agreements (client page, Service agreements tab), laid
// out like the Service agreements list (components/Tables/ServiceAgreements).
// The client column becomes a location column: every row is this client's.
const COLUMNS = SERVICE_AGREEMENT_COLUMNS.map((column) => (
  column.area === 'client'
    ? { area: 'location', label: 'Location', width: 'minmax(190px, 1.3fr)', bone: 'single', icon: 'map-marker' }
    : column
));

const CARDS = {
  ...SERVICE_AGREEMENT_CARDS,
  areas: SERVICE_AGREEMENT_CARDS.areas.map((row) => row.replace(/\bclient\b/g, 'location')),
};

const MIN_TABLE_WIDTH = 1100;

const STATUS_TONES = { active: 'green', completed: 'blue', draft: 'grey', cancelled: 'red', paused: 'amber' };

const statusLabel = (value) =>
  String(value || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

// "Every 3 months on the 1st" from the stored recurrence rule.
const repeatText = (rrule) => {
  if (!rrule) return null;
  try {
    const text = rrulestr(rrule).toText();
    return text.charAt(0).toUpperCase() + text.slice(1);
  } catch (error) {
    return null;
  }
};

// "10:21am" → "10:21 AM"
const clock = (value) => (value ? String(value).replace(/\s*(am|pm)$/i, (m, p) => ` ${p.toUpperCase()}`) : null);

const dayLabel = (date) => {
  const time = currentTime(date);
  return time.format(time.isSame(currentTime(), 'year') ? 'ddd, MMM D' : 'ddd, MMM D, YYYY');
};

@inject('viewStore', 'serviceAgreementStore') @observer
class ServiceAgreementsTable extends React.Component {

  componentDidMount() {
    this.props.viewStore.clientServiceAgreementSearch.where.client_id = this.props.client.attributes.id;
    this.props.serviceAgreementStore.loadClientServiceAgreements();
  }

  onPaginationChange(newPage) {
    const { viewStore } = this.props;
    viewStore.clientServiceAgreementSearch = {
      ...viewStore.clientServiceAgreementSearch,
      page: newPage,
    };
    this.props.serviceAgreementStore.loadClientServiceAgreements();
  }

  render() {
    const {
      viewStore,
      serviceAgreementStore: {
        clientServiceAgreements: { objects, isLoading, isError, meta },
      },
    } = this.props;

    if (isLoading && objects.length === 0) {
      return (
        <DataList.Loading
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          noun="service agreements"
          rows={5}
        />
      );
    }
    if (isError) {
      return (
        <DataList.LoadError
          noun="service agreements"
          onRetry={() => this.onPaginationChange((meta && meta.page) || 1)}
        />
      );
    }
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="service agreements"
          message="Recurring work you set up for this client, like maintenance plans, will show up here."
        />
      );
    }

    const pagination = meta && meta.total_pages > 1 && (
      <Footer>
        <div className="bp3-navbar-group bp3-align-left">
          <Pagination value={meta.page} total={meta.total_pages} onChange={this.onPaginationChange.bind(this)} />
        </div>
      </Footer>
    );

    return (
      <React.Fragment>
        <DataList.List
          preferTable
          columns={COLUMNS}
          cards={CARDS}
          minTableWidth={MIN_TABLE_WIDTH}
          total={(meta && meta.total_records) || objects.length}
          noun="service agreements"
        >
          {objects.map((serviceAgreement) => {
            const attributes = serviceAgreement.attributes;
            const serviceType = attributes.service_type || {};
            const location = attributes.location || {};
            const nextVisit = (attributes.jobs || [])[0];
            const start = clock(attributes.formatted_start_time);
            const end = clock(attributes.formatted_end_time);
            const status = attributes.status;
            return (
              <DataList.Row
                key={attributes.id}
                accent={serviceType.name ? accentFor(serviceType.color) : undefined}
              >
                <DataList.Cell area="number">
                  <div className="schedule-number">
                    <DataList.RowLink
                      href={`/dashboard/service_agreements/${attributes.id}/show`}
                      label={`Service agreement ${attributes.number}: ${attributes.title || ''}`}
                    >
                      {attributes.number}
                    </DataList.RowLink>
                    {serviceType.name && (
                      <div className="schedule-number__type">
                        <span
                          className="schedule-number__dot"
                          style={{ backgroundColor: accentFor(serviceType.color) }}
                        />
                        <span className="dl-truncate">{serviceType.name}</span>
                      </div>
                    )}
                  </div>
                </DataList.Cell>
                <DataList.Cell area="agreement">
                  <DataList.Lines
                    primary={attributes.title || 'Untitled agreement'}
                    secondary={attributes.reason_for_call}
                  />
                </DataList.Cell>
                <DataList.Cell area="schedule">
                  <DataList.Lines
                    primary={repeatText(attributes.rrule) || 'No repeat set'}
                    strong={false}
                    secondary={start ? `${start}${end ? ` – ${end}` : ''}` : null}
                  />
                </DataList.Cell>
                <DataList.Cell area="next">
                  {nextVisit ? (
                    <DataList.Lines
                      primary={<React.Fragment><DataList.CardOnly>Next visit </DataList.CardOnly>{dayLabel(nextVisit.start_date)}</React.Fragment>}
                      strong={false}
                      secondary={nextVisit.event_number ? `#${nextVisit.event_number}` : null}
                    />
                  ) : (
                    <DataList.Muted>No upcoming visits</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="location">
                  {location.full_address ? (
                    <DataList.Lines primary={location.full_address} strong={false} />
                  ) : (
                    <DataList.Muted>No location</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="price">
                  {attributes.price != null ? (
                    <span className="dl-lines__primary">
                      <DataList.CardOnly>Price </DataList.CardOnly>
                      {currencyFormattedAmount(attributes.price)}
                    </span>
                  ) : (
                    <DataList.Muted>No price</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="status">
                  {status && <DataList.Pill tone={STATUS_TONES[status] || 'grey'}>{statusLabel(status)}</DataList.Pill>}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[{
                      icon: 'edit',
                      label: 'View or edit service agreement',
                      onClick: () => viewStore.serviceAgreementShow({ uuid: attributes.id }, attributes.number),
                    }]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        {pagination}
      </React.Fragment>
    );
  }
}

export default ServiceAgreementsTable;
